import { useId, useSyncExternalStore, type CSSProperties } from 'react';
import clsx from 'clsx';
import { MdCheck } from 'react-icons/md';
import Switch from '../../atoms/Switch';
import { scaledVoiceLevel, type IVoiceLevelSource } from './voiceLevel';
import './SoundSettings.scss';

export interface ISoundDevice {
    deviceId: string;
    label: string;
}

/** Выбранное устройство; подпись помнится, чтобы показать отключённое */
export interface IChosenSoundDevice {
    deviceId: string;
    label?: string;
}

export interface ISoundDevices {
    inputs: ISoundDevice[];
    outputs: ISoundDevice[];
    /** Устройство системы по умолчанию — второй строкой у «Как в системе» */
    defaultInput?: string;
    defaultOutput?: string;
    /** Браузер назвал устройства; иначе под списком — `texts.labelsHidden` */
    labeled: boolean;
}

export interface ISoundSettingsTexts {
    systemInput: string;
    systemOutput: string;
    labelsHidden: string;
}

const DEFAULT_TEXTS: ISoundSettingsTexts = {
    systemInput: 'Как в системе',
    systemOutput: 'Как в системе',
    labelsHidden: 'Названия появятся после включения микрофона',
};

export interface ISoundSettingsProps {
    devices: ISoundDevices;
    input: IChosenSoundDevice | null;
    output: IChosenSoundDevice | null;
    noiseSuppression: boolean;
    /** Браузер умеет выбирать динамики и назвал хотя бы один — список «Динамики» */
    outputSelectable: boolean;
    onInputChange: (device: IChosenSoundDevice | null) => void;
    onOutputChange: (device: IChosenSoundDevice | null) => void;
    onNoiseSuppressionChange: (enabled: boolean) => void;
    /** Микрофон включён: вместо «включите микрофон, чтобы проверить» — индикатор `level` */
    micOn: boolean;
    level?: IVoiceLevelSource;
    /** Подписи пакета показа (`VOICE_DEVICE_TEXT`) */
    texts?: ISoundSettingsTexts;
    className?: string;
    /** Тест-якорь корня; части — `<id>-level`, `<id>-noise` @default 'sound-settings' */
    'data-testid'?: string;
}

interface IDeviceListProps {
    title: string;
    devices: ISoundDevice[];
    systemLabel: string;
    systemDevice?: string;
    chosen: IChosenSoundDevice | null;
    onChoose: (device: IChosenSoundDevice | null) => void;
}

/** Выбор устройства: системное и названные браузером; выбранное, но отключённое — с пометкой, чтобы было видно. */
function DeviceList({ title, devices, systemLabel, systemDevice, chosen, onChoose }: IDeviceListProps) {
    const titleId = useId();
    const missing = chosen && !devices.some((device) => device.deviceId === chosen.deviceId) ? chosen : null;
    const rows: { device: IChosenSoundDevice | null; label: string; detail?: string; note?: string }[] = [
        { device: null, label: systemLabel, detail: systemDevice },
        ...devices.map((device) => ({ device, label: device.label })),
        ...(missing
            ? [{ device: missing, label: missing.label ?? 'Выбранное устройство', note: 'не подключено' }]
            : []),
    ];
    const selected = chosen?.deviceId ?? '';
    return (
        <div className="rf-sound-settings__group">
            <div className="rf-sound-settings__title" id={titleId}>
                {title}
            </div>
            <div role="radiogroup" aria-labelledby={titleId} className="rf-sound-settings__list">
                {rows.map(({ device, label, detail, note }) => {
                    const checked = (device?.deviceId ?? '') === selected;
                    return (
                        <button
                            key={device?.deviceId ?? ''}
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            aria-label={detail ? `${label} · ${detail}` : undefined}
                            className="rf-sound-settings__option"
                            onClick={() => onChoose(device ? { deviceId: device.deviceId, label: device.label } : null)}
                        >
                            <span className="rf-sound-settings__check" aria-hidden>
                                {checked && <MdCheck size={16} />}
                            </span>
                            <span className="rf-sound-settings__text">
                                <span className="rf-sound-settings__label">{label}</span>
                                {detail && <span className="rf-sound-settings__detail">{detail}</span>}
                            </span>
                            {note && <span className="rf-sound-settings__note">{note}</span>}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/** Уровень своего микрофона — полоса; перерисовывается сама, мимо меню. */
function LevelMeter({ level, testId }: { level: IVoiceLevelSource; testId: string }) {
    const value = scaledVoiceLevel(useSyncExternalStore(level.subscribe, level.get));
    return (
        <span
            className="rf-sound-settings__meter"
            data-testid={testId}
            style={{ '--rf-sound-level': value.toFixed(2) } as CSSProperties}
        >
            <span className="rf-sound-settings__meter-fill" />
        </span>
    );
}

/**
 * Меню микрофона сеанса поддержки (IMPLEMENTATION.md §42) — у пользователя и у специалиста: микрофон, уровень, динамики
 * (где браузер умеет их выбирать), шумоподавление. Выбор применяется на лету; хранит и применяет его приложение.
 */
export default function SoundSettings({
    devices,
    input,
    output,
    noiseSuppression,
    outputSelectable,
    onInputChange,
    onOutputChange,
    onNoiseSuppressionChange,
    micOn,
    level,
    texts = DEFAULT_TEXTS,
    className,
    'data-testid': testId = 'sound-settings',
}: ISoundSettingsProps) {
    return (
        <div className={clsx('rf-sound-settings', className)} data-testid={testId}>
            <DeviceList
                title="Микрофон"
                devices={devices.inputs}
                systemLabel={texts.systemInput}
                systemDevice={devices.defaultInput}
                chosen={input}
                onChoose={onInputChange}
            />
            {!devices.labeled && <p className="rf-sound-settings__hint">{texts.labelsHidden}</p>}
            <div className="rf-sound-settings__level">
                <span>Уровень</span>
                {micOn && level ? (
                    <LevelMeter level={level} testId={`${testId}-level`} />
                ) : (
                    <span className="rf-sound-settings__hint">включите микрофон, чтобы проверить</span>
                )}
            </div>
            {outputSelectable && (
                <DeviceList
                    title="Динамики"
                    devices={devices.outputs}
                    systemLabel={texts.systemOutput}
                    systemDevice={devices.defaultOutput}
                    chosen={output}
                    onChoose={onOutputChange}
                />
            )}
            <Switch
                className="rf-sound-settings__switch"
                size="sm"
                between
                reverse
                label="Шумоподавление"
                description="Приглушает клавиатуру, вентилятор и шум улицы"
                checked={noiseSuppression}
                onCheckedChange={onNoiseSuppressionChange}
                data-testid={`${testId}-noise`}
            />
        </div>
    );
}
